import { FormEvent, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useTheme } from "../context/ThemeContext";

const BASE = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

type AccountForm = {
  accountName: string;
  accessType: string;
};

type AccountRow = {
  id: number | string;
  account_name: string;
  access_type: string;
  created_by_email: string;
  updated_by_email: string;
  created_at: string;
  updated_at: string;
};

type ToastState = {
  type: "success" | "error";
  message: string;
};

const EMPTY_ACCOUNT_FORM: AccountForm = {
  accountName: "",
  accessType: "",
};

const PAGE_SIZE = 10;

const formatDate = (value: string): string => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const AccountManagement = () => {
  const { isDarkMode } = useTheme();

  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [accountsCount, setAccountsCount] = useState(0);
  const [accountsLoading, setAccountsLoading] = useState(false);

  const [accessTypeOptions, setAccessTypeOptions] = useState<string[]>([]);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<AccountForm>(EMPTY_ACCOUNT_FORM);
  const [editingAccountId, setEditingAccountId] = useState("");
  const [editForm, setEditForm] = useState<AccountForm>(EMPTY_ACCOUNT_FORM);
  const [deleteConfirmAccountId, setDeleteConfirmAccountId] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [actionInProgress, setActionInProgress] = useState("");
  const [toast, setToast] = useState<ToastState | null>(null);
  const [page, setPage] = useState(1);

  const pageCount = Math.max(1, Math.ceil(accounts.length / PAGE_SIZE));

  const pagedAccounts = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return accounts.slice(start, start + PAGE_SIZE);
  }, [accounts, page]);

  const showToast = (type: ToastState["type"], message: string) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 4000);
  };

  const getApiErrorDetail = (error: unknown): string | null => {
    const maybeDetail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
    return typeof maybeDetail === "string" ? maybeDetail : null;
  };

  const getApiStatus = (error: unknown): number | null => {
    const maybeStatus = (error as { response?: { status?: unknown } })?.response?.status;
    return typeof maybeStatus === "number" ? maybeStatus : null;
  };

  const fetchAccessTypeOptions = async () => {
    try {
      const response = await axios.get(`${BASE}/api/account-management/access-types`, {
        withCredentials: true,
      });
      const options = Array.isArray(response.data?.items)
        ? (response.data.items as string[])
        : [];
      setAccessTypeOptions(options);
    } catch (error) {
      console.error("Failed to load access type options:", error);
      setAccessTypeOptions([]);
    }
  };

  const fetchAccounts = async () => {
    setAccountsLoading(true);
    try {
      const response = await axios.get(`${BASE}/api/account-management/accounts`, {
        withCredentials: true,
      });
      const nextAccounts = Array.isArray(response.data?.items)
        ? (response.data.items as AccountRow[])
        : [];
      setAccounts(nextAccounts);
      setAccountsCount(Number(response.data?.count || nextAccounts.length));
      setPage(1);
    } catch (error) {
      setAccounts([]);
      setAccountsCount(0);
      if (getApiStatus(error) === 401) {
        showToast("error", "Session expired. Please login again.");
      } else {
        showToast("error", "Failed to load accounts.");
      }
    } finally {
      setAccountsLoading(false);
    }
  };

  useEffect(() => {
    void fetchAccounts();
    void fetchAccessTypeOptions();
  }, []);

  const openCreate = () => {
    setCreateForm(EMPTY_ACCOUNT_FORM);
    setEditingAccountId("");
    setDeleteConfirmAccountId("");
    setIsCreateOpen(true);
  };

  const openEdit = (account: AccountRow) => {
    setEditForm({
      accountName: account.account_name || "",
      accessType: account.access_type || "",
    });
    setEditingAccountId(String(account.id || ""));
    setDeleteConfirmAccountId("");
    setIsCreateOpen(false);
  };

  const openDelete = (account: AccountRow) => {
    setDeleteConfirmAccountId(String(account.id || ""));
    setEditingAccountId("");
    setIsCreateOpen(false);
  };

  const closeCreatePanel = () => setIsCreateOpen(false);

  const cancelInlineEdit = () => {
    setEditingAccountId("");
    setEditForm(EMPTY_ACCOUNT_FORM);
  };

  const cancelInlineDelete = () => setDeleteConfirmAccountId("");

  const handleCreate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(
        `${BASE}/api/account-management/accounts`,
        { account_name: createForm.accountName, access_type: createForm.accessType },
        { withCredentials: true },
      );
      showToast("success", "Account created successfully.");
      setCreateForm(EMPTY_ACCOUNT_FORM);
      setIsCreateOpen(false);
      await fetchAccounts();
    } catch (error) {
      showToast("error", getApiErrorDetail(error) || "Failed to create account.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleInlineUpdate = async (accountId: number | string) => {
    const normalizedAccountId = String(accountId).trim();
    if (!normalizedAccountId) {
      showToast("error", "Invalid account ID.");
      return;
    }

    setActionInProgress(`edit-${normalizedAccountId}`);
    try {
      await axios.put(
        `${BASE}/api/account-management/accounts/${normalizedAccountId}`,
        { account_name: editForm.accountName, access_type: editForm.accessType },
        { withCredentials: true },
      );
      showToast("success", "Account updated successfully.");
      setEditingAccountId("");
      setEditForm(EMPTY_ACCOUNT_FORM);
      await fetchAccounts();
    } catch (error) {
      showToast("error", getApiErrorDetail(error) || "Failed to update account.");
    } finally {
      setActionInProgress("");
    }
  };

  const handleInlineDelete = async (accountId: number | string) => {
    const normalizedAccountId = String(accountId).trim();
    if (!normalizedAccountId) {
      showToast("error", "Invalid account ID.");
      return;
    }

    setActionInProgress(`delete-${normalizedAccountId}`);
    try {
      await axios.delete(`${BASE}/api/account-management/accounts/${normalizedAccountId}`, {
        withCredentials: true,
      });
      showToast("success", "Account deleted successfully.");
      setDeleteConfirmAccountId("");
      await fetchAccounts();
    } catch (error) {
      showToast("error", getApiErrorDetail(error) || "Failed to delete account.");
    } finally {
      setActionInProgress("");
    }
  };

  const inputClass =
    "w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-900 outline-none transition focus:border-lime-400 focus:bg-white focus:ring-2 focus:ring-lime-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:focus:border-lime-500";
  const labelClass =
    "mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-300";

  const renderCreateForm = () => (
    <form onSubmit={handleCreate} className="space-y-4">
      <div>
        <label className={labelClass}>
          Account Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={createForm.accountName}
          onChange={(e) =>
            setCreateForm((prev) => ({ ...prev, accountName: e.target.value }))
          }
          placeholder="Enter account name"
          required
          className={inputClass}
        />
      </div>

      <div>
        <label className={labelClass}>
          Access Type <span className="text-red-500">*</span>
        </label>
        <select
          value={createForm.accessType}
          onChange={(e) =>
            setCreateForm((prev) => ({ ...prev, accessType: e.target.value }))
          }
          required
          className={inputClass}
        >
          <option value="">Select access type</option>
          {accessTypeOptions.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </div>

      <div className="flex items-center gap-3 pt-2">
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center justify-center rounded-xl bg-lime-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-lime-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? "Saving..." : "Create Account"}
        </button>
        <button
          type="button"
          onClick={closeCreatePanel}
          className="inline-flex items-center justify-center rounded-xl border border-gray-200 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
        >
          Cancel
        </button>
      </div>
    </form>
  );

  return (
    <div
      className={`${
        isDarkMode ? "bg-black" : "bg-[#f4fbf6]"
      } min-h-screen w-full relative overflow-hidden`}
    >
      {!isDarkMode && (
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -top-16 -left-10 h-56 w-56 rounded-full bg-emerald-100/70 blur-3xl" />
          <div className="absolute top-20 right-10 h-48 w-48 rounded-full bg-sky-100/70 blur-3xl" />
          <div className="absolute bottom-8 left-1/3 h-44 w-44 rounded-full bg-lime-100/60 blur-3xl" />
        </div>
      )}

      <div className="relative z-10 mx-auto max-w-7xl px-6 py-10">
        <div className="pb-6 text-center">
          <span className="inline-flex items-center rounded-full border border-lime-300/80 bg-lime-50 px-4 py-1 text-xs font-bold uppercase tracking-wide text-lime-800 dark:border-lime-700 dark:bg-lime-900/40 dark:text-lime-200">
            Admin Panel
          </span>
          <h2 className="mt-3 text-2xl font-extrabold text-gray-900 dark:text-white md:text-3xl">
            Account Management
          </h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
            List of accounts and quick actions to create, update, and delete.
          </p>
        </div>

        {toast && (
          <div
            className={`mb-6 flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold shadow ${
              toast.type === "success"
                ? "border border-lime-300 bg-lime-50 text-lime-800 dark:border-lime-700 dark:bg-lime-900/30 dark:text-lime-200"
                : "border border-red-300 bg-red-50 text-red-800 dark:border-red-700 dark:bg-red-900/30 dark:text-red-200"
            }`}
          >
            <span>{toast.type === "success" ? "✓" : "✕"}</span>
            <span>{toast.message}</span>
          </div>
        )}

        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            Total Accounts: {accountsCount}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={fetchAccounts}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
            >
              Refresh
            </button>
            <button
              type="button"
              onClick={openCreate}
              className="rounded-lg bg-lime-600 px-3 py-2 text-xs font-semibold text-white hover:bg-lime-700"
            >
              + Create Account
            </button>
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-[#dbe7f3] bg-white shadow-[0_10px_24px_rgba(16,24,40,0.08)] dark:border-gray-800 dark:bg-gray-950">
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-[#1f3550] text-white">
                <tr>
                  <th className="px-4 py-3 font-semibold">Account ID</th>
                  <th className="px-4 py-3 font-semibold">Account Name</th>
                  <th className="px-4 py-3 font-semibold">Access Type</th>
                  <th className="px-4 py-3 font-semibold">Created By</th>
                  <th className="px-4 py-3 font-semibold">Updated By</th>
                  <th className="px-4 py-3 font-semibold">Created Date</th>
                  <th className="px-4 py-3 font-semibold">Updated Date</th>
                  <th className="px-4 py-3 text-center font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {accountsLoading && (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-8 text-center text-gray-500 dark:text-gray-400"
                    >
                      Loading accounts...
                    </td>
                  </tr>
                )}

                {!accountsLoading && pagedAccounts.length === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-8 text-center text-gray-500 dark:text-gray-400"
                    >
                      No accounts found.
                    </td>
                  </tr>
                )}

                {!accountsLoading &&
                  pagedAccounts.map((account, index) => {
                    const accountId = String(account.id);
                    const isEditing = editingAccountId === accountId;
                    const isDeleteConfirm = deleteConfirmAccountId === accountId;
                    const serialId = (page - 1) * PAGE_SIZE + index + 1;

                    return (
                      <tr
                        key={accountId}
                        className="border-b border-[#edf3f9] text-[#5b7293] dark:border-gray-800 dark:text-gray-200"
                      >
                        <td className="px-4 py-3 font-semibold">{serialId}</td>
                        <td className="px-4 py-3 font-semibold text-[#223b5a] dark:text-white">
                          {isEditing ? (
                            <input
                              value={editForm.accountName}
                              onChange={(e) =>
                                setEditForm((prev) => ({
                                  ...prev,
                                  accountName: e.target.value,
                                }))
                              }
                              className="w-full rounded border border-gray-300 px-2 py-1 text-xs text-gray-900"
                            />
                          ) : (
                            account.account_name || "-"
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {isEditing ? (
                            <select
                              value={editForm.accessType}
                              onChange={(e) =>
                                setEditForm((prev) => ({
                                  ...prev,
                                  accessType: e.target.value,
                                }))
                              }
                              className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-900"
                            >
                              <option value="">Select</option>
                              {accessTypeOptions.map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span
                              className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-bold ${
                                account.access_type === "Private"
                                  ? "bg-blue-100 text-blue-700"
                                  : "bg-amber-100 text-amber-700"
                              }`}
                            >
                              {account.access_type || "-"}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">{account.created_by_email || "-"}</td>
                        <td className="px-4 py-3">{account.updated_by_email || "-"}</td>
                        <td className="px-4 py-3">{formatDate(account.created_at)}</td>
                        <td className="px-4 py-3">{formatDate(account.updated_at)}</td>
                        <td className="px-4 py-3">
                          {isEditing ? (
                            <div className="flex items-center justify-center gap-2">
                              <button
                                type="button"
                                onClick={() => void handleInlineUpdate(account.id)}
                                disabled={actionInProgress === `edit-${accountId}`}
                                className="rounded bg-lime-600 px-2 py-1 text-xs font-semibold text-white disabled:opacity-60"
                              >
                                {actionInProgress === `edit-${accountId}`
                                  ? "Saving"
                                  : "Save"}
                              </button>
                              <button
                                type="button"
                                onClick={cancelInlineEdit}
                                className="rounded border border-gray-300 px-2 py-1 text-xs font-semibold text-gray-700"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : isDeleteConfirm ? (
                            <div className="flex items-center justify-center gap-2">
                              <button
                                type="button"
                                onClick={() => void handleInlineDelete(account.id)}
                                disabled={actionInProgress === `delete-${accountId}`}
                                className="rounded bg-red-600 px-2 py-1 text-xs font-semibold text-white disabled:opacity-60"
                              >
                                {actionInProgress === `delete-${accountId}`
                                  ? "Deleting"
                                  : "Confirm"}
                              </button>
                              <button
                                type="button"
                                onClick={cancelInlineDelete}
                                className="rounded border border-gray-300 px-2 py-1 text-xs font-semibold text-gray-700"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center justify-center gap-2">
                              <button
                                type="button"
                                onClick={() => openEdit(account)}
                                className="rounded p-1 text-sky-600 hover:bg-sky-50"
                                title="Update account"
                              >
                                <svg
                                  className="h-4 w-4"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                  />
                                </svg>
                              </button>
                              <button
                                type="button"
                                onClick={() => openDelete(account)}
                                className="rounded p-1 text-red-600 hover:bg-red-50"
                                title="Delete account"
                              >
                                <svg
                                  className="h-4 w-4"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                  />
                                </svg>
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between border-t border-[#edf3f9] px-4 py-3 text-sm text-gray-600 dark:border-gray-800 dark:text-gray-300">
            <span>
              Rows per page <strong>{PAGE_SIZE}</strong>
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="rounded-full border border-gray-300 px-3 py-1 text-xs font-semibold disabled:opacity-40"
              >
                Prev
              </button>
              {Array.from({ length: pageCount }, (_, i) => i + 1)
                .slice(Math.max(0, page - 3), Math.max(0, page - 3) + 5)
                .map((pageNo) => (
                  <button
                    key={pageNo}
                    type="button"
                    onClick={() => setPage(pageNo)}
                    className={`h-8 w-8 rounded-full text-xs font-bold ${
                      pageNo === page
                        ? "bg-[#1f3550] text-white"
                        : "border border-gray-300 text-gray-700"
                    }`}
                  >
                    {pageNo}
                  </button>
                ))}
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                disabled={page === pageCount}
                className="rounded-full border border-gray-300 px-3 py-1 text-xs font-semibold disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        </div>

        {isCreateOpen && (
          <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-6 shadow-[0_10px_26px_rgba(16,24,40,0.08)] dark:border-gray-800 dark:bg-gray-950">
            <div className="mb-5 flex items-center gap-2 text-[15px] font-semibold text-gray-800 dark:text-gray-100">
              <span className="inline-block h-4 w-[3px] rounded-full bg-lime-500" />
              <span>Create New Account</span>
            </div>
            {renderCreateForm()}
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountManagement;